/**
 * parseSvgColors(svgText)
 *
 * Extracts the ordered, deduplicated set of recolorable fill colors from an
 * SVG's <style> block and returns both the display colors and the instructions
 * needed to apply replacements.
 *
 * Returns an array of ColorSlot objects:
 *   {
 *     color:   string   — hex color shown in the picker (e.g. '#666' or '#000000')
 *     type:    'replace' | 'inject'
 *     value:   string   — for 'replace': the exact hex string to find-and-replace
 *                         for 'inject':  the CSS selector to inject a fill rule into
 *                         (e.g. '.cls-3')
 *   }
 *
 * 'replace' slots: the SVG has an explicit `fill: #hex` declaration — we swap the hex.
 * 'inject'  slots: the class has no fill declaration (renders as SVG default black).
 *                  We add `.cls-X { fill: <color>; }` before </style>.
 */
export function parseSvgColors(svgText) {
    const styleMatch = svgText.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    if (!styleMatch) return [];
    const styleText = styleMatch[1];

    const slots = [];
    const seenColors = new Set();   // dedup by hex
    const seenSelectors = new Set(); // dedup injected selectors

    // ── Pass 1: collect explicit fill: #hex declarations ─────────────────
    // Match each individual CSS rule block with its selector
    // e.g.  .cls-1 { fill: #666; }
    const singleRuleRe = /([^{}]+)\{([^}]*)\}/g;
    let m;
    while ((m = singleRuleRe.exec(styleText)) !== null) {
        const body = m[2];

        const hexMatch = body.match(/fill\s*:\s*(#[0-9a-fA-F]{3,8})\b/);
        if (!hexMatch) continue;

        const hexOriginal = hexMatch[1];          // preserve case for find-and-replace
        const hex = hexOriginal.toLowerCase();    // normalised for display / dedup
        // Skip fill: none and fill: url(...)
        if (hex === 'none') continue;

        if (!seenColors.has(hex)) {
            seenColors.add(hex);
            slots.push({ color: hex, type: 'replace', value: hexOriginal });
        }
    }

    // ── Pass 2: find classes with fill-rule but no fill → inject black ────
    // Re-scan rule blocks, looking for ones that:
    //   1. Contain fill-rule
    //   2. Have NO fill: #hex  (not already handled above)
    //   3. Have NO fill: none or fill: url(
    //   4. Have a single simple class selector (e.g. .cls-3)
    while ((m = singleRuleRe.exec(styleText)) !== null) {
        // (reset needed because singleRuleRe was exhausted above)
    }
    singleRuleRe.lastIndex = 0;

    while ((m = singleRuleRe.exec(styleText)) !== null) {
        const selector = m[1].trim();
        const body = m[2];

        const hasFillRule = /fill-rule/.test(body);
        const hasFillHex  = /fill\s*:\s*#/.test(body);
        const hasFillSkip = /fill\s*:\s*(none|url\()/.test(body);

        if (!hasFillRule || hasFillHex || hasFillSkip) continue;

        // Might be a combined selector like ".cls-1, .cls-2, .cls-3"
        // Split on commas and find any that don't already have their own fill
        const parts = selector.split(',').map((s) => s.trim()).filter(Boolean);
        for (const part of parts) {
            if (seenSelectors.has(part)) continue;

            // Check if this class already has its own fill slot (from pass 1 above)
            // by seeing if a separate rule exists for it
            const escapedPart = part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Look for a rule that targets ONLY this selector and has a fill hex
            const ownFillRe = new RegExp(escapedPart + '\\s*\\{[^}]*fill\\s*:\\s*#[0-9a-fA-F]', 'i');
            if (ownFillRe.test(styleText)) continue; // already covered

            seenSelectors.add(part);
            const defaultBlack = '#000000';
            if (!seenColors.has(defaultBlack)) {
                seenColors.add(defaultBlack);
            }
            slots.push({ color: defaultBlack, type: 'inject', value: part });
        }
    }

    return slots;
}

/**
 * applyColorSlots(svgText, slots, chosenColors)
 *
 * Given raw SVG text, the slots array from parseSvgColors, and the user's
 * chosen colors (string[] parallel to slots), returns recolored SVG text.
 *
 *   slots        — ColorSlot[]  from parseSvgColors
 *   chosenColors — string[]     user-chosen hex per slot (falls back to slot.color)
 */
export function applyColorSlots(svgText, slots, chosenColors) {
    let result = svgText;

    // Collect all inject rules to append before </style>
    const injectRules = [];

    slots.forEach((slot, i) => {
        const replacement = chosenColors[i] || slot.color;
        if (replacement === slot.color) return; // no change needed

        if (slot.type === 'replace') {
            result = result.split(slot.value).join(replacement);
        } else {
            // inject: add an overriding CSS rule
            injectRules.push(`${slot.value} { fill: ${replacement}; }`);
        }
    });

    if (injectRules.length > 0) {
        result = result.replace(
            /(<\/style>)/i,
            `      ${injectRules.join('\n      ')}\n    $1`
        );
    }

    return result;
}
